import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { Loading } from "../../../../components";
import Editor from "../../../../components/editor/Editor";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import Select from "../../../../components/select/Select";
import TimedMessage from "../../../../components/timed-message/TimedMessage";
import {
    GetPortalSettingsDocument, Permission, PortalIndexProvider,
    useGetPortalSettingsQuery, useSavePortalSettingsMutation,
} from "../../../../__generated__/api";
import { ErrorRemote } from "../../../error/Error";

const PORTAL_INDEXES = [
    {
        label: "Open AI",
        value: PortalIndexProvider.OpenAi,
    },
    {
        label: "None",
        value: PortalIndexProvider.None,
    },
];

export default function PortalSettings(): ReactElement {
    const { data, error, loading: isLoading } = useGetPortalSettingsQuery();
    const [save] = useSavePortalSettingsMutation();

    const [isSaved, setIsSaved] = useState(false);

    const [title, setTitle] = useState(() => data?.portal?.title ?? "");
    const [welcomeMessage, setWelcomeMessage] = useState(() => data?.portal?.welcomeMessage ?? "");
    const [publicPermissions, setPublicPermissions] = useState(() => data?.portal?.publicPermissions ?? []);
    const [guestPermissions, setGuestPermissions] = useState(() => data?.portal?.guestPermissions ?? []);
    const [publicFormIds, setPublicFormIds] = useState(data?.portal?.publicFormIds ?? []);
    const [guestFormIds, setGuestFormIds] = useState(data?.portal?.guestFormIds ?? []);
    const [isSharedChannelGuest, setIsSharedChannelGuest] = useState(data?.portal?.isSharedChannelGuest ?? false);

    const [
        portalIndexProvider,
        setPortalIndexProvider,
    ] = useState(() => data?.portal?.portalIndexProvider ?? PortalIndexProvider.None);

    useEffect(() => {
        setTitle(data?.portal?.title ?? "");
        setWelcomeMessage(data?.portal?.welcomeMessage ?? "");
        setPublicPermissions(data?.portal?.publicPermissions ?? []);
        setGuestPermissions(data?.portal?.guestPermissions ?? []);

        setPublicFormIds(data?.portal?.publicFormIds ?? []);
        setGuestFormIds(data?.portal?.guestFormIds ?? []);

        setPortalIndexProvider(data?.portal?.portalIndexProvider ?? PortalIndexProvider.None);
        setIsSharedChannelGuest(data?.portal?.isSharedChannelGuest ?? false);
    }, [data?.portal]);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        (async () => {
            await save({
                refetchQueries: [
                    { query: GetPortalSettingsDocument },
                ],
                variables: {
                    portal: {
                        title,
                        welcomeMessage,
                        publicPermissions,
                        guestPermissions,
                        publicFormIds,
                        guestFormIds,
                        portalIndexProvider,
                        isSharedChannelGuest,
                    },
                },
            });
            setIsSaved(true);
        })();
    };
    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const createListModifier = (
        current: Permission[],
        onChange: (permissions: Permission[]) => void,
        ...permission: Permission[]
    ) => (value: boolean) => {
        let copy = Array.from(current ?? []);
        if (value) {
            copy = copy.concat(permission);
        } else {
            const set = new Set(permission);
            copy = copy.filter(item => !set.has(item));
        }
        onChange(copy.dedupe());
    };

    return (
        <form className="flex-grow-1" onSubmit={onSubmit}>
            <TimedMessage shouldShow={isSaved} onClose={() => setIsSaved(false)}>
                Portal settings save
            </TimedMessage>

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    General Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Title</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="My Portal"
                        required
                        onChange={evt => setTitle(evt.target.value)}
                        value={title as string}
                    />
                    <Form.Text>
                        The title of the portal displayed to all users that visit the site.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Welcome message</Form.Label>

                    <Editor
                        className="border border-gray rounded"
                        text={welcomeMessage}
                        onChange={welcome => setWelcomeMessage(welcome)}
                        placeholder={`Welcome to ${title ?? "Our site"}`}
                    />
                    <Form.Text>
                        The first message that a user sees when visiting the portal.
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label className="fw-bolder">Search Index Provider</Form.Label>

                    <Select
                        className="text-gray"
                        placeholder=""
                        onChange={option => setPortalIndexProvider(option?.value as PortalIndexProvider)}
                        options={PORTAL_INDEXES}
                        value={PORTAL_INDEXES.find(option => option.value === portalIndexProvider)}
                    />
                    <Form.Text>
                        The Search Indexer is provided by a 3rd party service, OpenAI, LLC. View their
                        {" "}
                        <a href="https://openai.com/api/policies/privacy/" target="_blank" rel="noreferrer">Privacy Policy</a>
                        {" "}
                        to understand how Open AI handles data.
                    </Form.Text>

                </Form.Group>

            </div>
            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light ">
                    Guest Permissions
                </h5>
                <p>
                    Guests are users who have access to Slack but are not Agents or Administrators. By default,
                    all users who have access to your applications such as Slack or Microsoft Teams are guests.
                </p>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Read Articles</Form.Label>
                    {guestPermissions?.includes(Permission.ArticleReadPublic) && (
                        <div className={guestPermissions?.includes(Permission.ArticleReadPublic) ? "alert alert-info" : ""}>
                            <Form.Text className="text-primary">
                                All published articles are public and visible to everyone who is able to login
                                to this Portal as a guest.
                            </Form.Text>
                        </div>
                    )}

                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={guestPermissions?.includes(Permission.ArticleReadPublic) ?? false}
                        onChange={createListModifier(guestPermissions, setGuestPermissions, Permission.ArticleReadPublic)}
                    />
                    {!guestPermissions?.includes(Permission.ArticleReadPublic) && (
                        <Form.Text>
                            If enabled, all published articles become available to any guest that has access to Slack.
                            All accounts and guest accounts who have access to your Slack
                            workspace will have access to these articles
                        </Form.Text>
                    )}

                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Create tickets</Form.Label>

                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={guestPermissions?.includes(Permission.TicketCreateGuest) ?? false}
                        onChange={createListModifier(
                            guestPermissions,
                            setGuestPermissions,
                            Permission.TicketCreateGuest,
                            Permission.TicketsReadSelf,
                        )}
                    />
                    <Form.Text>
                        If enabled guests will be able to create tickets as themselves.
                    </Form.Text>

                </Form.Group>

                {guestPermissions?.includes(Permission.TicketCreateGuest) && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Request Types</Form.Label>

                        <RequestTypes
                            isMulti
                            placeholder="The request types that guests can create."
                            onSelected={selected => setGuestFormIds(selected)}
                            value={guestFormIds as string[]}
                        />
                        <Form.Text>
                            Specify which request types guest users are able to create.
                            To order the request types, click and drag them inside this box.
                        </Form.Text>

                    </Form.Group>
                )}

                {guestPermissions?.includes(Permission.ArticleReadPublic) && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Slack Connect users are guests</Form.Label>

                        <BooleanSelect
                            value={isSharedChannelGuest}
                            onChange={value => setIsSharedChannelGuest(value ?? false)}
                        />
                        <Form.Text>
                            If yes, users coming from indirect sources such as Slack Connect channels will
                            be considered guests and will be able to view articles and create tickets.
                        </Form.Text>

                    </Form.Group>
                )}

            </div>
            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light ">
                    Public Permissions
                </h5>
                <p>
                    Public permissions control what unauthenticated people on the internet
                    are able to view.
                </p>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">All Articles are Public</Form.Label>
                    {publicPermissions?.includes(Permission.ArticleReadPublic) && (
                        <div className={publicPermissions?.includes(Permission.ArticleReadPublic) ? "alert alert-danger" : ""}>
                            <Form.Text className="text-primary">
                                All published articles are public and visible to everyone on the internet.
                            </Form.Text>
                        </div>
                    )}

                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={publicPermissions?.includes(Permission.ArticleReadPublic) ?? false}
                        onChange={createListModifier(publicPermissions, setPublicPermissions, Permission.ArticleReadPublic)}
                    />
                    {!publicPermissions?.includes(Permission.ArticleReadPublic) && (
                        <Form.Text className="text-danger">
                            If enabled, all published articles become public to the internet.
                            Everyone in the world will be allowed to read all articles.
                        </Form.Text>
                    )}

                </Form.Group>

            </div>

            <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                <button type="submit" className="btn btn-primary">
                    Save
                </button>
            </div>

        </form>
    );
}
